<script lang="ts">
  import axios from 'axios';
  import Bouton from '../ui/Bouton.svelte';
  import ChampTexte from '../ui/ChampTexte.svelte';
  import { fabriqueFilAriane, type PropriétésFilAriane } from '../ui/filAriane';
  import Formulaire from '../ui/Formulaire.svelte';
  import Heros from '../ui/Heros.svelte';
  import ModaleNouvelleSessionGroupe from './ModaleNouvelleSessionGroupe.svelte';
  import type { ReponseCreationSessionGroupe } from './SessionGroupe';

  let codeSession = $state('');
  let creationNouvelleSessionEnCours = $state(false);
  let modaleNouvelleSession: ModaleNouvelleSessionGroupe | undefined = $state();
  let formulaire: Formulaire | undefined = $state();
  let statut: 'default' | 'valid' | 'error' | 'info' | undefined = $state();

  const nouvelleSession = async () => {
    try {
      creationNouvelleSessionEnCours = true;
      const reponse = await axios.post<ReponseCreationSessionGroupe>('/api/sessions-groupe');
      modaleNouvelleSession?.ouvre(reponse.data);
    } finally {
      creationNouvelleSessionEnCours = false;
    }
  };

  const rejoindreSession = async () => {
    if (formulaire?.estValide()) {
      const codeSansTiret = codeSession.replaceAll('-', '');
      try {
        await axios.get(`/api/sessions-groupe/${codeSansTiret}`);
        window.location.href = `/test-maturite?session-groupe=${codeSansTiret}`;
      } catch {
        statut = 'error';
      }
    }
  };

  $effect(() => {
    const codeAvecTiret = codeSession.length === 3 && !codeSession.endsWith('-') ? `${codeSession}-` : codeSession;
    const codeFormate = codeAvecTiret.toUpperCase();
    if (codeSession !== codeFormate) codeSession = codeFormate;
  });

  const propriétésFilAriane: PropriétésFilAriane = [
    {
      nom: 'Faire le test !',
      lien: '/faire-le-test',
    },
    {
      nom: 'Test de maturité cyber',
      lien: '/test-maturite',
    },
    {
      nom: 'Session de groupe',
    },
  ];
</script>

<Heros
  titre="Session de groupe"
  description="Déterminez la maturité cyber de votre organisation en 5 minutes."
  format="banniere"
  theme="clair"
  segmentsFilAriane={fabriqueFilAriane(propriétésFilAriane)}
/>

<dsfr-container>
  <div class="contenu-section">
    <div class="bloc-role organisateur">
      <div class="role">Organisateur</div>
      <h3>Lancer une session de groupe</h3>
      <p>
        <b> Vous organisez un atelier ou un événement autour de la cybersécurité ? </b>
        <br /><br />
        Générez un code de session à partager avec les participants. Vous pourrez ensuite comparer les résultats obtenus.
      </p>
      <Bouton
        libelle="Nouvelle session"
        type="secondaire"
        taille="md"
        desactive={creationNouvelleSessionEnCours}
        surClic={nouvelleSession}
      />
      <ModaleNouvelleSessionGroupe bind:this={modaleNouvelleSession} />
    </div>

    <div class="bloc-role participant">
      <div class="role">Participant</div>
      <h3>Rejoindre une session de groupe</h3>
      <p>
        <b>Vous participez à une session de groupe ?</b>
        <br /><br />
        Saisissez le code de session partagé par votre organisateur pour accéder au test de maturité cyber. Votre résultat
        est anonyme.
      </p>
      <Formulaire bind:this={formulaire}>
        <div class="code-session">
          <ChampTexte
            id="codeSession"
            libelle="Code de session"
            messageErreur="Renseigner un code de session en cours valide."
            modele={'[A-Z1-9]{3}-?[A-Z1-9]{3}'}
            nom="codeSession"
            requis={true}
            sousTitre="Saisissez le code fourni par votre organisateur"
            tailleMaximale={7}
            bind:valeur={codeSession}
            bind:statut
          />
        </div>
        <Bouton libelle="Débuter le test" type="primaire" taille="md" surClic={rejoindreSession} />
      </Formulaire>
    </div>
  </div>
</dsfr-container>

<style lang="scss">
  @use '../../../assets/styles/responsive' as *;

  .contenu-section {
    padding: 56px 0 56px;
    display: flex;
    flex-direction: column;
    gap: 24px;
    box-sizing: border-box;
    @include a-partir-de(lg) {
      flex-direction: row;
    }
  }

  .bloc-role {
    padding: 40px;
    border: 1px solid var(--border-default-grey);
    border-radius: 8px;

    &.organisateur {
      background-color: #f6f6f6;
      border-color: #f6f6f6;
    }

    &.participant {
      .code-session {
        margin-bottom: 24px;
      }
    }

    h3 {
      margin: 4px 0 8px;
    }

    p {
      margin: 0 0 32px;
    }

    :global(.champ-session-groupe) {
      width: 100%;
      @include a-partir-de(sm) {
        width: auto;
      }
    }

    .role {
      font-size: 0.875rem;
      line-height: 1.5rem;
      text-transform: uppercase;
      font-weight: bold;
      color: #666666;
    }
  }
</style>
